// Notification-open telemetry (ED-2570 follow-on).
//
// WHY THIS EXISTS. The push funnel used to stop at "delivered": push_receipts
// records sent → delivered from the Expo receipt, and nothing after it. The tap
// itself — the moment a notification actually did its job — was handled by
// RootNavigator's handleNotificationResponse and then thrown away, so no query
// could tell a push that worked from one nobody saw. That is the whole
// difference between "we sent 40,000 reminders" and "reminders work".
//
// WHY AT THE TOP OF THE HANDLER RATHER THAN PER BRANCH. handleNotificationResponse
// has around ten early returns — medication Mark Taken / Snooze / Skip, wellness
// "I'm OK", question and refill dismissals, the wellness check-in overlay, voice
// call approval, permission requests, chat routing — and more will be added. Every
// one of them is a user interacting with a notification, and expo fires that
// handler for nothing else. One call at the entry point covers all of them and
// cannot drift out of step with a branch someone adds later.
//
// PHI. The detail carries the payload's `category` and the action-button id and
// NOTHING ELSE. A medication reminder's title and body contain the drug name,
// which is PHI; they are never read here. `category` is additionally required to
// look like a slug before it ships, so an unexpected payload cannot turn a free
// text field into an exfiltration path — it degrades to 'unknown' instead.
//
// Recording is gated by recorder.enabled, i.e. system_settings
// .session_telemetry_enabled, exactly like every other action event. Events land
// in app_session_events (event_type 'action', name 'notification_opened') and are
// subject to that table's retention window — count opens over a rolling window,
// not since the beginning of time.

// Type-only: this module needs the response SHAPE, not the native module, so it
// stays importable anywhere (web, tests) without pulling expo-notifications in.
import type * as Notifications from 'expo-notifications';
import { recorder } from '../services/sessionTelemetry/recorder';

export const NOTIFICATION_OPENED = 'notification_opened';

// expo's identifier for "the user tapped the notification body" as opposed to one
// of its action buttons. Compared as a literal rather than imported so a change in
// the constant degrades to reporting the raw id, never to a crash.
const DEFAULT_ACTION = 'expo.modules.notifications.actions.DEFAULT';

// A category is product metadata ('medication_reminder', 'chat_message', …). Anything
// that is not slug-shaped is not a category we ship, whatever the payload claims.
const CATEGORY_RE = /^[a-z0-9_]{1,40}$/i;

/**
 * The dedupe window for cold starts.
 *
 * A tap that launches the app from a killed state can arrive twice: once from
 * Notifications.getLastNotificationResponse(), which RootNavigator reads precisely
 * because the response listener is not registered yet at that point, and once from
 * the listener if it does manage to register in time. RootNavigator's own
 * handledInitialNotificationRef guards its cold-start block from re-running, but
 * not the listener from also firing for the same notification — so without this,
 * every cold start would have a chance of counting as two opens and inflating the
 * one number this instrumentation exists to produce.
 */
const seen: string[] = [];
const SEEN_MAX = 20;

export function __resetNotificationTelemetry(): void {
  seen.length = 0;
}

/** Exported for tests: the detail shipped for one response, or null to skip it. */
export function notificationOpenDetail(
  response: Notifications.NotificationResponse | null | undefined,
): { category: string; action: string; reminder_type?: string } | null {
  const request = (response as any)?.notification?.request;
  if (!request) return null;

  const data: Record<string, any> = request?.content?.data ?? {};
  const rawCategory = data?.category;
  const category = typeof rawCategory === 'string' && CATEGORY_RE.test(rawCategory)
    ? rawCategory
    : 'unknown';

  const rawAction = (response as any)?.actionIdentifier;
  const action = !rawAction || rawAction === DEFAULT_ACTION
    ? 'tap'
    : String(rawAction).slice(0, 40);

  // Which KIND of reminder, when the payload says. category alone collapses
  // re-engagement nudges, medication reminders, refill prompts and wake/bedtime
  // check-ins into one bucket called 'reminder', so without this the open rate
  // for re-engagement specifically cannot be separated from every other nudge —
  // and re-engagement is the campaign whose whole point is measuring whether it
  // brings anyone back. The server already puts reminder_type in the payload
  // next to category; it was simply never reported.
  const rawType = (data as any)?.reminder_type;
  const reminderType = typeof rawType === 'string' && CATEGORY_RE.test(rawType) ? rawType : null;

  // Omitted rather than nulled when absent, so the common case ships two keys.
  return reminderType ? { category, action, reminder_type: reminderType } : { category, action };
}

/** Exported for tests: whether this response is a repeat of one already counted. */
export function isDuplicateOpen(response: Notifications.NotificationResponse | null | undefined): boolean {
  const id = String((response as any)?.notification?.request?.identifier ?? '');
  if (!id) return false; // no identifier to dedupe on — count it rather than drop it
  if (seen.includes(id)) return true;
  seen.push(id);
  if (seen.length > SEEN_MAX) seen.shift();
  return false;
}

/**
 * The web twin. expo-notifications has no cold-launch concept in a browser, so web
 * notification clicks arrive as a service-worker NOTIFICATION_CLICKED postMessage
 * that DesktopLayout handles instead. Instrumenting only the native path would
 * make every web open invisible and quietly bias the open rate toward mobile —
 * which is the exact kind of half-measurement this change exists to remove.
 *
 * Dedupe is deliberately skipped: the postMessage carries no notification id, and
 * the browser delivers one message per click, so there is nothing to double up.
 */
export function trackWebNotificationOpen(rawCategory: unknown): void {
  try {
    const category = typeof rawCategory === 'string' && CATEGORY_RE.test(rawCategory)
      ? rawCategory
      : 'unknown';
    recorder.track(NOTIFICATION_OPENED, { category, action: 'tap' });
  } catch {
    // best-effort
  }
}

/**
 * Record that the user opened a notification. Call once, at the top of the
 * response handler — never per branch.
 *
 * Never throws: instrumentation must not be able to break the medication action,
 * the wellness ack, or the navigation that the user actually asked for.
 */
export function trackNotificationOpen(
  response: Notifications.NotificationResponse | null | undefined,
): void {
  try {
    const detail = notificationOpenDetail(response);
    if (!detail) return;
    if (isDuplicateOpen(response)) return;
    recorder.track(NOTIFICATION_OPENED, detail);
  } catch {
    // best-effort
  }
}
